import { motion } from 'framer-motion';
import { CSSProperties, memo, ReactNode, useMemo, useState } from 'react';
import { SpaceKind, SpaceNavigationRoot } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  ChevronDownIcon,
  GlobeIcon,
  HomeLineIcon,
  LockIcon,
  VaultFolderIcon,
  WorkspaceFolderIcon,
} from '../../../../assets/icons/index';
import { VaultFolderIconVariant } from '../../../../assets/icons/vault-folder-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../../components/collapsible/index';
import { LoadingCrossfade } from '../../../../components/loading-skeleton/index';
import { Cursor, Tooltip } from '../../../../components/tooltip/index';
import { VaultAccess } from '../../../../guards/page-access.util';
import { StringKey } from '../../../../lang/index';
import { useRecipientNavigation } from '../../hooks/use-recipient-navigation.hook';
import {
  DataRoomViewer,
  useDataRoomViewer,
  useVaultAccess,
} from '../../hooks/use-recipient-view.hook';
import { useSpaceNavigationFolders } from '../../hooks/use-space-navigation-folders.hook';
import { useSpacesNavigation } from '../../hooks/use-spaces-navigation.hook';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { SpaceBrowserCrumb, SpaceSelection, WorkspacesNavId } from '../../types/data-room.types';
import {
  ancestorsOf,
  nodeIdOf,
  selectedNodeId,
  sidebarRowPaddingClass,
  sidebarWidth,
  visibleSidebarDepth,
} from '../../utils/sidebar-tree.util';
import { isVaultSharedFolder } from '../../vault/utils/vault-shared-folder.util';
import { SpaceFolderIcon } from './space-folder-icon';
import { WorkspaceSectionLabel } from './workspace-section-label';
import {
  SIDEBAR_ROWS_PLACEHOLDER_HEIGHT_PX,
  SidebarRowsPlaceholder,
  SidebarSectionPlaceholder,
} from './workspaces-loading-placeholders';

const NO_ROOTS: SpaceNavigationRoot[] = [];
const NO_CRUMBS: SpaceBrowserCrumb[] = [];

const TREE_ICON_COLOR = '#475467';
const TREE_SELECTED_COLOR = '#2565C8';

const SpaceKindIcon = ({ kind }: { kind: SpaceKind }) => {
  if (kind === SpaceKind.VAULT) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center">
        <VaultFolderIcon iconColor={TREE_ICON_COLOR} variant={VaultFolderIconVariant.COMPACT} />
      </span>
    );
  }

  if (kind === SpaceKind.WORKSPACE) {
    return <WorkspaceFolderIcon className="size-4" height={16} width={16} />;
  }

  return <GlobeIcon className="size-4" height={16} iconColor="#2565C8" width={16} />;
};

type SidebarTreeRowProps = {
  // The collapsible mounts it only while the row is open, so a level loads on first expand.
  children?: ReactNode;
  icon: ReactNode;
  isExpandable: boolean;
  isLocked?: boolean;
  name: string;
  // What opening the row reveals; the row's id, depth and ancestors all follow from it.
  selection: SpaceSelection;
};

// Each row subscribes to its own store slices, so opening or selecting one row re-renders that row
// only; the shell above subscribes to the width alone.
const SidebarTreeRow = ({
  children,
  icon,
  isExpandable,
  isLocked = false,
  name,
  selection,
}: SidebarTreeRowProps) => {
  const id = nodeIdOf(selection);
  const depth = selection.breadcrumbs.length;
  const reveal = SidebarTreeStore((state) => state.reveal);
  const revealOrCollapse = SidebarTreeStore((state) => state.revealOrCollapse);
  const setOpen = SidebarTreeStore((state) => state.setOpen);
  const isExpanded = SidebarTreeStore((state) => id in state.expanded);
  const isSelected = SidebarTreeStore((state) => selectedNodeId(state.selection) === id);
  const labelId = `workspace-sidebar-${id}`;

  const row = (
    <div
      className={twMerge(
        'flex h-9 w-full items-center justify-between rounded-lg text-left text-sm text-gray-700 transition-colors duration-200 ease-out',
        sidebarRowPaddingClass(depth),
        isSelected && 'bg-brand-50 text-brand-700',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {isExpandable ? (
          <CollapsibleTrigger aria-labelledby={labelId}>
            <ChevronDownIcon
              className="size-4 shrink-0"
              iconColor={isSelected ? TREE_SELECTED_COLOR : TREE_ICON_COLOR}
            />
          </CollapsibleTrigger>
        ) : (
          <span className="size-4 shrink-0" />
        )}
        <button
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-workspace-nav={id}
          // A row with nothing below it has nothing to fold, so a second click keeps it as it is.
          onClick={() => (isExpandable ? revealOrCollapse(selection) : reveal(selection))}
          type="button"
        >
          {icon}
          {/* The full name on hover, once the row's width cuts it short. */}
          <Tooltip content={name} cursor={Cursor.POINTER} placement="right">
            <span
              className={twMerge(
                'min-w-0 flex-1 truncate leading-5',
                isSelected ? 'font-[550]' : 'font-[450]',
              )}
              id={labelId}
            >
              {name}
            </span>
          </Tooltip>
        </button>
      </div>
      {isLocked && (
        <LockIcon className="size-4 shrink-0" height={16} iconColor={TREE_ICON_COLOR} width={16} />
      )}
    </div>
  );

  if (!isExpandable) {
    return row;
  }

  return (
    <Collapsible
      onOpenChange={(open) => setOpen(id, open, ancestorsOf(selection))}
      open={isExpanded}
    >
      {row}
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
};

type SidebarFolderLevelProps = {
  // The path to this level's parent; empty for a space's top level.
  breadcrumbs: SpaceBrowserCrumb[];
  space: SpaceNavigationRoot;
};

const SidebarFolderLevel = ({ breadcrumbs, space }: SidebarFolderLevelProps) => {
  const { folders, isPending } = useSpaceNavigationFolders({
    kind: space.kind,
    parentFolderId: breadcrumbs[breadcrumbs.length - 1]?.id ?? null,
    spaceId: space.id,
  });
  // The collapsible animates only the opening, so rows replacing a skeleton grow from its height.
  const [openedOnSkeleton] = useState(isPending);

  return (
    <LoadingCrossfade
      className="h-auto"
      isLoading={isPending}
      skeleton={<SidebarRowsPlaceholder depth={breadcrumbs.length + 1} />}
    >
      {() => (
        <motion.div
          animate={{ height: 'auto' }}
          className="overflow-hidden"
          initial={openedOnSkeleton ? { height: SIDEBAR_ROWS_PLACEHOLDER_HEIGHT_PX } : false}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {folders.map((folder) => {
            const folderBreadcrumbs = [...breadcrumbs, { id: folder.id, name: folder.name }];

            return (
              <SidebarTreeRow
                icon={
                  <SpaceFolderIcon
                    className="size-4"
                    isShared={space.kind === SpaceKind.VAULT && isVaultSharedFolder(folder.name)}
                    size={16}
                  />
                }
                isExpandable={folder.hasChildren}
                key={folder.id}
                name={folder.name}
                selection={{ breadcrumbs: folderBreadcrumbs, folderId: folder.id, space }}
              >
                <SidebarFolderLevel breadcrumbs={folderBreadcrumbs} space={space} />
              </SidebarTreeRow>
            );
          })}
        </motion.div>
      )}
    </LoadingCrossfade>
  );
};

type SidebarSectionProps = {
  spaces: SpaceNavigationRoot[];
  title: StringKey;
};

const SidebarSection = memo(function SidebarSection({ spaces, title }: SidebarSectionProps) {
  if (!spaces.length) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <WorkspaceSectionLabel title={title} />
      <div className="flex w-full flex-col">
        {spaces.map((space) => (
          <SidebarTreeRow
            icon={<SpaceKindIcon kind={space.kind} />}
            isExpandable={space.hasChildren}
            isLocked={space.isLocked}
            key={space.id}
            name={space.name}
            selection={{ breadcrumbs: NO_CRUMBS, folderId: null, space }}
          >
            <SidebarFolderLevel breadcrumbs={NO_CRUMBS} space={space} />
          </SidebarTreeRow>
        ))}
      </div>
    </div>
  );
});

const SidebarHomeButton = () => {
  const resetSelection = SidebarTreeStore((state) => state.resetSelection);
  const isHomeSelected = SidebarTreeStore((state) => state.selection === undefined);

  return (
    <button
      className={twMerge(
        'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors duration-200 ease-out',
        isHomeSelected && 'bg-brand-50',
      )}
      data-workspace-nav={WorkspacesNavId.HOME}
      onClick={resetSelection}
      type="button"
    >
      <HomeLineIcon
        height={16}
        iconColor={isHomeSelected ? TREE_SELECTED_COLOR : TREE_ICON_COLOR}
        width={16}
      />
      <span
        className={twMerge(
          'text-sm',
          isHomeSelected ? 'font-[550] text-brand-700' : 'font-[450] text-gray-700',
        )}
      >
        <AppFormattedMessage id="HOME" />
      </span>
    </button>
  );
};

export const WorkspacesSidebar = () => {
  const viewer = useDataRoomViewer();
  const isRecipientView = viewer === DataRoomViewer.RECIPIENT;
  // A Stakeholder is a recipient of the rooms shared with them and reads their own folders of the
  // Vault beside them.
  const vaultAccess = useVaultAccess();
  const hasOwnVault = isRecipientView && vaultAccess === VaultAccess.OWN_FOLDERS;
  const { isPending: isOwnerPending, navigation } = useSpacesNavigation({
    enabled: viewer === DataRoomViewer.OWNER || hasOwnVault,
  });
  const { dataRooms: recipientRooms, isPending: isRecipientPending } = useRecipientNavigation({
    enabled: viewer === DataRoomViewer.RECIPIENT,
  });
  // A number, not the `expanded` map: the shell re-renders only when the width actually changes.
  const width = SidebarTreeStore((state) => sidebarWidth(visibleSidebarDepth(state.expanded)));
  // Stable references, so the memoised sections skip re-rendering when only the width changes.
  const workspaceRoots = useMemo(
    () =>
      navigation && !isRecipientView ? [navigation.vault, ...navigation.workspaces] : NO_ROOTS,
    [isRecipientView, navigation],
  );
  const ownVaultRoots = useMemo(
    () => (navigation && hasOwnVault ? [navigation.vault] : NO_ROOTS),
    [hasOwnVault, navigation],
  );
  const dataRooms = isRecipientView ? recipientRooms : (navigation?.dataRooms ?? NO_ROOTS);
  const isPending = viewer === DataRoomViewer.UNRESOLVED || isOwnerPending || isRecipientPending;

  return (
    <aside
      className="flex h-full w-full shrink-0 flex-col gap-6 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/50 p-4 transition-[width] duration-300 ease-out lg:w-[var(--workspaces-sidebar-width)] lg:rounded-l-xl lg:rounded-br-xl lg:rounded-tr-none"
      style={{ '--workspaces-sidebar-width': `${width}px` } as CSSProperties}
    >
      <SidebarHomeButton />
      <LoadingCrossfade
        className="h-auto"
        isLoading={isPending}
        skeleton={
          <div className="flex flex-col gap-6">
            <SidebarSectionPlaceholder />
            <SidebarSectionPlaceholder />
          </div>
        }
      >
        {() => (
          <div className="flex flex-col gap-6">
            {/* A recipient has no internal workspaces — the section is absent, not empty, because
                those are not theirs to know about. A Stakeholder has their own folders of the
                Vault, under no heading that names the workspaces. */}
            {!isRecipientView && (
              <SidebarSection spaces={workspaceRoots} title="INTERNAL_WORKSPACES" />
            )}
            <SidebarSection spaces={ownVaultRoots} title="VAULT" />
            <SidebarSection spaces={dataRooms} title="DATA_ROOMS" />
          </div>
        )}
      </LoadingCrossfade>
    </aside>
  );
};
